import React, { useMemo, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cmlPhaseData } from '../../data/diseasesData';
import { useUserInput } from '../../context/UserInputContext';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';
import YesNoToggle from '../../components/YesNoToggle';
import { Tone } from '../../types';

type CmlPhase = 'cp' | 'ap' | 'bp' | null;

const HIGH_RISK_MARKER = '\n\nHigh risk indicators\n';

const PHASE_LABEL: Record<'cp' | 'ap' | 'bp', string> = {
  cp: 'Chronic Phase (CP)',
  ap: 'Accelerated Phase (AP)',
  bp: 'Blast Phase (BP)',
};

const PHASE_TONE: Record<'cp' | 'ap' | 'bp', Tone> = {
  cp: 'green',
  ap: 'yellow',
  bp: 'red',
};

const CmlDiagnosisTable: React.FC = () => {
  const { t } = useTranslation('hematology');
  const { state, update } = useUserInput();
  const d = state.cmlDiagnosis;
  const set = (patch: Partial<typeof d>) =>
    update('cmlDiagnosis', { ...d, ...patch });

  const [highRiskOpen, setHighRiskOpen] = useState(false);
  const [acaInfoOpen, setAcaInfoOpen] = useState(false);

  const phases = useMemo(() => {
    const b = parseFloat(d.blasts);
    const baso = parseFloat(d.basophils);
    const hasBlasts = !isNaN(b);
    const hasBaso = !isNaN(baso);

    // WHO 2022 — no accelerated phase. Blast phase is defined positively;
    // chronic phase requires an actual blast count, so a lone "no" answer to
    // one of the yes/no questions no longer declares the patient chronic.
    let who: CmlPhase = null;
    if (
      (hasBlasts && b >= 20) ||
      d.extramedullary === true ||
      d.lymphoblasts === true
    ) {
      who = 'bp';
    } else if (hasBlasts && b < 20) {
      who = 'cp';
    }

    // ELN — BP ≥30%, AP 15–29% / basophils ≥20% / platelets <100 unrelated to
    // treatment / major-route ACA emerging on treatment.
    let eln: CmlPhase = null;
    if ((hasBlasts && b >= 30) || d.extramedullary === true) {
      eln = 'bp';
    } else if (
      (hasBlasts && b >= 15) ||
      (hasBaso && baso >= 20) ||
      d.plateletsLow === true ||
      d.acaOnTreatment === true
    ) {
      eln = 'ap';
    } else if (hasBlasts && b < 15) {
      eln = 'cp';
    }

    return { who, eln };
  }, [d]);

  const phaseFor = (source: string) =>
    source === 'WHO 2022' ? phases.who : phases.eln;

  const renderPhaseText = (content: string) => {
    if (!content.includes(HIGH_RISK_MARKER)) {
      return content.split('\n').map((line, i, arr) => (
        <span key={i}>
          {line}
          {i < arr.length - 1 && <br />}
        </span>
      ));
    }

    const [mainText, highRiskText] = content.split(HIGH_RISK_MARKER);
    return (
      <>
        {mainText.split('\n').map((line, i, arr) => (
          <span key={i}>
            {line}
            {i < arr.length - 1 && <br />}
          </span>
        ))}

        <button
          type="button"
          className={`high-risk-toggle ${highRiskOpen ? 'is-open' : ''}`}
          onClick={() => setHighRiskOpen((v) => !v)}
          aria-expanded={highRiskOpen}
        >
          <ChevronDown size={14} strokeWidth={2.5} />
          High risk indicators
        </button>

        {highRiskOpen && (
          <span className="high-risk-details">
            {highRiskText.split('\n').map((line, i, arr) => (
              <span key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </span>
            ))}
          </span>
        )}
      </>
    );
  };

  const inputs = (
    <>
      <NumberField
        label={t('cml.blastsLabel')}
        value={d.blasts}
        onChange={(v) => set({ blasts: v })}
        placeholder={t('cml.blastsPlaceholder')}
        min={0}
        max={100}
        step={0.1}
        decimal
      />

      <NumberField
        label={t('cml.basophilsLabel')}
        value={d.basophils}
        onChange={(v) => set({ basophils: v })}
        placeholder={t('cml.basophilsPlaceholder')}
        hint={t('cml.basophilsHint')}
        min={0}
        max={100}
        step={0.1}
        decimal
      />

      <YesNoToggle
        label={
          <>
            {t('cml.acaQuestion')}{' '}
            <small>(ELN)</small>
            <button
              type="button"
              className="aca-info-btn"
              onClick={(e) => {
                e.preventDefault();
                setAcaInfoOpen((v) => !v);
              }}
              aria-expanded={acaInfoOpen}
              aria-label={t('cml.acaDetails')}
            >
              <Info size={15} />
            </button>
          </>
        }
        value={d.acaOnTreatment}
        onChange={(v) => set({ acaOnTreatment: v })}
      />

      {acaInfoOpen && (
        <div className="aca-info-panel" dir="ltr">
          <strong>Major route ACA:</strong>
          <ul>
            <li>Second Philadelphia chromosome</li>
            <li>Trisomy 8</li>
            <li>Isochromosome 17q</li>
            <li>Trisomy 19</li>
            <li>Complex karyotype</li>
            <li>Abnormalities of 3q26.2</li>
          </ul>
        </div>
      )}

      <YesNoToggle
        label={t('cml.plateletsQuestion')}
        value={d.plateletsLow}
        onChange={(v) => set({ plateletsLow: v })}
      />

      <YesNoToggle
        label={t('cml.extramedullaryQuestion')}
        value={d.extramedullary}
        onChange={(v) => set({ extramedullary: v })}
      />

      <YesNoToggle
        label={
          <>
            {t('cml.lymphoblastsQuestion')} <small>(WHO)</small>
          </>
        }
        value={d.lymphoblasts}
        onChange={(v) => set({ lymphoblasts: v })}
      />
    </>
  );

  return (
    <CalcShell
      title={t('cml.phaseTitle')}
      subtitle="Chronic Phase (CP), Accelerated Phase (AP), Blast Phase (BP)"
      inputs={inputs}
      aside={
        phases.who || phases.eln ? (
          <div className="phase-verdicts">
            {(['WHO 2022', 'ELN'] as const).map((source) => {
              const phase = phaseFor(source);
              if (!phase) return null;
              return (
                <div
                  key={source}
                  className={`phase-verdict tone--${PHASE_TONE[phase]}`}
                >
                  <span className="phase-verdict__source">{source}</span>
                  <span className="phase-verdict__phase" dir="ltr">
                    {PHASE_LABEL[phase]}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="risk-empty-hint">
            {t('cml.emptyHint')}
          </p>
        )
      }
    >
      {cmlPhaseData.map((row) => {
        const active = phaseFor(row.source);
        const phaseRows = [
          { key: 'cp' as const, content: row.chronicPhase },
          { key: 'ap' as const, content: row.acceleratedPhase },
          { key: 'bp' as const, content: row.blastPhase },
        ];

        return (
          <section className="ref-block" key={row.source}>
            <h4 className="ref-block__title">{row.source}</h4>

            <div className="phase-grid">
              {phaseRows.map((phase) => (
                <article
                  key={phase.key}
                  className={[
                    'phase-card',
                    `tone--${PHASE_TONE[phase.key]}`,
                    active === phase.key ? 'is-active' : '',
                    active && active !== phase.key ? 'is-muted' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <h5 className="phase-card__title" dir="ltr">
                    {PHASE_LABEL[phase.key]}
                  </h5>
                  <div className="phase-card__body" dir="ltr">
                    {renderPhaseText(phase.content)}
                  </div>
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </CalcShell>
  );
};

export default CmlDiagnosisTable;
